import React from 'react';
import KPICard from '../../components/shared/KPICard';
import StatusPill from '../../components/shared/StatusPill';
import SectionDivider from '../../components/shared/SectionDivider';
import { rmsDevices } from '../../data/mockData';
import { PlantStatus } from '../../types';
import { formatDate, relativeTime } from '../../utils/dateHelpers';

export default function VendorDeviceHealth() {
  const expiredCount = rmsDevices.filter(d => d.daysToExpiry < 0).length;
  const expiringSoonCount = rmsDevices.filter(d => d.daysToExpiry >= 0 && d.daysToExpiry <= 30).length;
  const offlineCount = rmsDevices.filter(d => d.status === PlantStatus.OFFLINE).length;

  const getCertColor = (days: number) => {
    if (days < 0) return 'text-danger font-semibold';
    if (days <= 30) return 'text-warning font-semibold';
    return 'text-success';
  };

  const getStatusType = (status: PlantStatus): 'online' | 'offline' | 'warning' => {
    if (status === PlantStatus.ONLINE) return 'online';
    if (status === PlantStatus.OFFLINE) return 'offline';
    return 'warning';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex gap-4">
        <KPICard title="Total RMS Devices" value={rmsDevices.length} subtext="Zebron Solar portfolio" />
        <KPICard title="Certs Expired" value={expiredCount} bgColor="bg-red-50" subtextColor="red" subtext="Immediate renewal" />
        <KPICard title="Expiring ≤30 days" value={expiringSoonCount} bgColor="bg-orange-50" subtextColor="amber" subtext="Schedule renewal" />
        <KPICard title="Offline Devices" value={offlineCount} bgColor="bg-red-50" subtextColor="red" subtext="Last seen >24h" />
      </div>

      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <SectionDivider title="RMS Device Health" subtitle="Certificate expiry & firmware status" />
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2.5 text-text-tertiary font-medium">Device IMEI</th>
                <th className="text-left py-2.5 text-text-tertiary font-medium">Plant ID</th>
                <th className="text-center py-2.5 text-text-tertiary font-medium">Status</th>
                <th className="text-left py-2.5 text-text-tertiary font-medium">Firmware</th>
                <th className="text-left py-2.5 text-text-tertiary font-medium">Cert Expiry</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">Days Left</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {rmsDevices.map(device => {
                const isExpired = device.daysToExpiry < 0;
                const isExpiringSoon = device.daysToExpiry >= 0 && device.daysToExpiry <= 30;
                return (
                  <tr
                    key={device.imei}
                    className={`border-b border-border/50 hover:bg-bg-secondary cursor-pointer ${isExpired ? 'bg-red-50/40' : isExpiringSoon ? 'bg-orange-50/30' : ''}`}
                  >
                    <td className="py-2.5 font-mono text-text-secondary">{device.imei}</td>
                    <td className="py-2.5 font-medium text-accent">{device.plantId}</td>
                    <td className="py-2.5 text-center">
                      <StatusPill status={getStatusType(device.status)} />
                    </td>
                    <td className="py-2.5">
                      <span className={`font-mono ${device.firmwareVersion < '2.4' ? 'text-warning' : 'text-text-primary'}`}>
                        v{device.firmwareVersion}
                      </span>
                      {device.firmwareVersion < '2.4' && (
                        <span className="ml-1.5 text-warning" style={{ fontSize: 10 }}>↑ Update</span>
                      )}
                    </td>
                    <td className={`py-2.5 ${getCertColor(device.daysToExpiry)}`}>
                      {formatDate(device.certExpiry)}
                      {isExpired && <span className="ml-1 text-danger" style={{ fontSize: 10 }}>(EXPIRED)</span>}
                    </td>
                    <td className={`py-2.5 text-right ${getCertColor(device.daysToExpiry)}`}>
                      {isExpired ? `${Math.abs(device.daysToExpiry)}d overdue` : `${device.daysToExpiry}d`}
                    </td>
                    <td className="py-2.5 text-right text-text-tertiary">{relativeTime(device.lastSeen)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
